<template>
    <div
        v-if="portalContentAndDesignStore.portalInitialized"
        :class="[
            'flex',
            portalContentAndDesignStore.liveDesignerEnabled ? 'overflow-hidden h-screen' : '',
            getBackgroundClasses
        ]"
        :style="getBackgroundStyle"
        v-cloak
    >
        <!-- Live Designer Sidebar -->
        <live-designer v-if="portalContentAndDesignStore.liveDesignerEnabled" />

        <!-- Main Content Area -->
        <div
            :class="[
                'flex-1',
                portalContentAndDesignStore.liveDesignerEnabled ? 'overflow-y-auto h-screen' : ''
            ]"
        >
            <div class="flex min-h-screen mx-auto max-w-7xl py-6 px-2 lg:py-8 lg:px-8">
                <!-- Content width -->
                <div class="mx-auto my-auto space-y-6 lg:space-y-10 w-full max-w-5xl">
                    <!-- Main content -->
                    <div v-if="showPageHeader" class="m-auto w-full space-y-6 lg:space-y-10 max-w-xl">
                        <div class="text-center space-y-2" :class="getPageTextColorClass">
                            <!-- Branding Section -->
                            <div v-if="brandingType && brandingType !== 'none'" class="flex justify-center py-3">
                                <!-- Symbol -->
                                <template v-if="brandingType === 'symbol' && symbolUrl">
                                    <div
                                        class="relative h-20 w-20 lg:h-32 lg:w-32 flex-shrink-0 bg-white bg-cover bg-center"
                                        :class="getButtonCorners"
                                        :style="{ backgroundImage: `url(${symbolUrl})` }"
                                    ></div>
                                </template>

                                <!-- Logo -->
                                <template v-else-if="brandingType === 'logo' && logoUrl">
                                    <div class="relative w-auto" style="max-width: 256px;">
                                        <img :src="logoUrl" class="w-auto h-auto" alt="Program logo" />
                                    </div>
                                </template>
                            </div>
                            <h2 class="text-xl lg:text-3xl font-semibold">
                                <editable-text-field
                                    v-model="pageHeading"
                                    text-type="span"
                                >
                                </editable-text-field>
                            </h2>
                            <div v-if="pageSubheader" class="partnero-page-content-section text-sm opacity-75 space-y-1">
                                <editable-text-editor-field
                                    v-model="pageSubheader"
                                >
                                </editable-text-editor-field>
                            </div>
                        </div>

                        <slot></slot>
                    </div>

                    <div v-else class="w-full">
                        <slot></slot>
                    </div>

                    <!-- Footer: Language selector and Partnero branding -->
                    <div v-if="portalContentAndDesignStore.isAuthView && (showPartneroBranding || showLanguageSelector)" class="flex justify-between items-center gap-4 w-full">
                        <!-- Language Selector -->
                        <div v-if="showLanguageSelector">
                            <language-selector></language-selector>
                        </div>
                        <div v-else></div>

                        <!-- Partnero Branding -->
                        <div v-if="showPartneroBranding" class="flex-none">
                            <a :href="partneroBrandingLink" target="_blank">
                                <div class="bg-dark-gray rounded flex items-center gap-2 p-2">
                                    <svg viewBox="0 0 80 120" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" class="h-4">
                                        <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                                            <g fill="#ffffff" fill-rule="nonzero">
                                                <path d="M10,20 L20,20 C42.0309024,20 59.8837209,37.9123279 59.8837209,60 C59.8837209,81.8667954 42.3861735,99.6413359 20.65966,99.99464 L20,100 L20,90 C36.5005917,90 49.8837209,76.5722604 49.8837209,60 C49.8837209,43.5934622 36.766916,30.2688692 20.4940722,30.004018 L20,30 L0,30 L0,0 L20,0 C53.137085,0 80,26.862915 80,60 C80,92.8057141 53.671657,119.462114 20.9922097,119.991962 L20,120 L0,120 L0,70 L20,70 C25.5228475,70 30,65.5228475 30,60 C30,54.5707601 25.6733292,50.1520683 20.2799048,50.0038418 L20,50 L0,50 L0,40 L20,40 C31.045695,40 40,48.954305 40,60 C40,70.9306357 31.2312714,79.813225 20.3444894,79.9970929 L20,80 L10,80 L10,110 L20,110 C47.3380951,110 69.5517619,88.0597142 69.9933014,60.8268414 L70,60 C70,32.6619049 48.0597142,10.4482381 20.8268414,10.0066986 L20,10 L10,10 L10,20 Z" id="Partnero"></path>
                                            </g>
                                        </g>
                                    </svg>
                                    <div class="text-white text-sm font-semibold">
                                        Runs on Partnero
                                    </div>
                                </div>
                            </a>
                        </div>
                    </div>

                    <!-- Partnero branding footer (for public pages) -->
                    <div v-if="!portalContentAndDesignStore.isAuthView && showPartneroBranding" class="flex justify-center items-center">
                        <div class="flex-none">
                            <a :href="partneroBrandingLink" target="_blank">
                                <div class="bg-dark-gray rounded flex items-center gap-2 p-2">
                                    <svg viewBox="0 0 80 120" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" class="h-4">
                                        <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                                            <g fill="#ffffff" fill-rule="nonzero">
                                                <path d="M10,20 L20,20 C42.0309024,20 59.8837209,37.9123279 59.8837209,60 C59.8837209,81.8667954 42.3861735,99.6413359 20.65966,99.99464 L20,100 L20,90 C36.5005917,90 49.8837209,76.5722604 49.8837209,60 C49.8837209,43.5934622 36.766916,30.2688692 20.4940722,30.004018 L20,30 L0,30 L0,0 L20,0 C53.137085,0 80,26.862915 80,60 C80,92.8057141 53.671657,119.462114 20.9922097,119.991962 L20,120 L0,120 L0,70 L20,70 C25.5228475,70 30,65.5228475 30,60 C30,54.5707601 25.6733292,50.1520683 20.2799048,50.0038418 L20,50 L0,50 L0,40 L20,40 C31.045695,40 40,48.954305 40,60 C40,70.9306357 31.2312714,79.813225 20.3444894,79.9970929 L20,80 L10,80 L10,110 L20,110 C47.3380951,110 69.5517619,88.0597142 69.9933014,60.8268414 L70,60 C70,32.6619049 48.0597142,10.4482381 20.8268414,10.0066986 L20,10 L10,10 L10,20 Z" id="Partnero"></path>
                                            </g>
                                        </g>
                                    </svg>
                                    <div class="text-white text-sm font-semibold">
                                        Runs on Partnero
                                    </div>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import {computed} from "vue";
import {usePortalContentAndDesignStore} from "../../../../stores/partners/portalContentAndDesignStore";
import EditableTextEditorField from "../../portal-editable-fields/EditableTextEditorField.vue";
import LiveDesigner from "../../live-designer/LiveDesigner.vue";

// Get current route name from URL path since referral portals don't use Vue Router
const getCurrentRouteName = () => {
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    if (path.includes('/login')) return 'partners.login.show';
    if (path.includes('/register')) return 'partners.register.show';
    return '';
};
const portalContentAndDesignStore = usePortalContentAndDesignStore();

const currentRouteName = computed(() => getCurrentRouteName());

// Check if current page should show header (login/register pages)
const showPageHeader = computed(() => {
    const routeName = currentRouteName.value;
    return ['partners.login.show', 'partners.register.show'].includes(routeName);
});

// Get page heading based on route
const pageHeading = computed({
    get() {
        const routeName = currentRouteName.value;
        if (routeName === 'partners.login.show') {
            return portalContentAndDesignStore.translationSettings?.portal_login_title ||
                   (portalContentAndDesignStore.translationSettings?.portal_translation_login_login_to
                    ? `${portalContentAndDesignStore.translationSettings.portal_translation_login_login_to} ${portalContentAndDesignStore.program?.name}`
                    : `Log in to ${portalContentAndDesignStore.program?.name || ''}`);
        } else if (routeName === 'partners.register.show') {
            return portalContentAndDesignStore.translationSettings?.portal_signup_title ||
                   `Register for ${portalContentAndDesignStore.program?.name || ''}`;
        }
        return '';
    },
    set(value) {
        const routeName = currentRouteName.value;
        if (routeName === 'partners.login.show') {
            portalContentAndDesignStore.translationSettings.portal_login_title = value;
        } else if (routeName === 'partners.register.show') {
            portalContentAndDesignStore.translationSettings.portal_signup_title = value;
        }
    }
});

// Get page subheader based on route
const pageSubheader = computed({
    get() {
        const routeName = currentRouteName.value;
        if (routeName === 'partners.login.show') {
            return portalContentAndDesignStore.translationSettings?.portal_login_description;
        } else if (routeName === 'partners.register.show') {
            return portalContentAndDesignStore.translationSettings?.portal_signup_description;
        }
        return '';
    },
    set(value) {
        const routeName = currentRouteName.value;
        if (routeName === 'partners.login.show') {
            portalContentAndDesignStore.translationSettings.portal_login_description = value;
        } else if (routeName === 'partners.register.show') {
            portalContentAndDesignStore.translationSettings.portal_signup_description = value;
        }
    }
});

// Background classes based on referral portal settings
const getBackgroundClasses = computed(() => {
    const backgroundColor = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_background_color || 'gray-50';
    const hasBackgroundImage = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_background_image;

    const classes = ['h-screen', 'overflow-auto'];

    if (backgroundColor.startsWith('#')) {
        classes.push('bg-custom-bg');
    } else {
        classes.push(`bg-${backgroundColor}`);
    }

    if (hasBackgroundImage) {
        classes.push('bg-cover', 'bg-center');
    }

    return classes.join(' ');
});

// Background style for custom background image
const getBackgroundStyle = computed(() => {
    const backgroundImage = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_background_image;
    if (backgroundImage) {
        return {
            backgroundImage: `url('${backgroundImage}')`
        };
    }
    return {};
});

// Page text color class
const getPageTextColorClass = computed(() => {
    const textColor = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_text_color || 'gray-800';

    if (textColor.startsWith('#')) {
        return 'text-custom-text';
    } else {
        return `text-${textColor}`;
    }
});

// Show Partnero branding
const showPartneroBranding = computed(() => {
    return portalContentAndDesignStore.partneroBrandingSettings?.enabled;
});

// Partnero branding link
const partneroBrandingLink = computed(() => {
    return portalContentAndDesignStore.partneroBrandingSettings?.unique_key_link || 'https://www.partnero.com';
});

// Show language selector (only for authenticated pages when multilanguage is enabled)
const showLanguageSelector = computed(() => {
    return portalContentAndDesignStore.isAuthView && portalContentAndDesignStore.multilanguageEnabled;
});

// Branding functionality
const brandingType = computed(() => {
    const routeName = currentRouteName.value;
    if (routeName === 'partners.login.show') {
        return portalContentAndDesignStore.loginPageSettings?.program_login_page_branding_type || 'none';
    }
    if (routeName === 'partners.register.show') {
        return portalContentAndDesignStore.signupPageSettings?.signup?.program_signup_page_branding_type || 'none';
    }
    return 'none';
});

const symbolUrl = computed(() => {
    return portalContentAndDesignStore.srcRoutes?.portal_layout_file_symbol || '';
});

const logoUrl = computed(() => {
    return portalContentAndDesignStore.srcRoutes?.portal_layout_file_logo || '';
});

const getButtonCorners = computed(() => {
    const corners = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_layout_corners || 'rounded-xl';
    return corners === 'none' ? 'rounded-sm' : corners;
});
</script>
